"use client";

import { Button, Card, Col, Row, Space, Tag, theme } from "antd";
import Text from "@/components/common/Typography/Text";
import { FC, ReactNode } from "react";
import FlightV3Icon from "@/components/icons/FlightIconV3";
import HotelsIcon from "@/components/icons/HotelsIcon";
import { OrderData } from "@/lib/redux/features/booking/type";
import { TypeSegment } from "@/lib/redux/features/flight/type";
import { groupSegments } from "@/utils/flight/groupSegments";
import { parseJson } from "@/utils/safeParse/parseJson";
import TimmerCheckout from "./TimmerCheckout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { useTERouter } from "@/hooks/app/useTERouter";
import { getDatetimeSegment } from "@/utils/flight/getDatetimeSegment";
import { useTranslation } from "react-i18next";
import { useTETranslation } from "@/hooks/app/useTETranslation";
import { useIsDesktop } from "@/hooks/app/useIsDesktop";
import { ProductType } from "@/constants/booking/order";
import type { TripPaymentState } from "./tripData";

type BookingStatus =
  | "COMPLETED"
  | "CONFIRMED"
  | "CANCELLED"
  | 'CANCEL'
  | "PENDING"
  | "ACTIVE"
  | "HOLD"
  | "FAIL"
  | string;

const BookingStatusLabel: Record<string, string> = {
  COMPLETED: "my_booking.status_completed",
  CONFIRMED: "my_booking.status_confirm",
  CANCELLED: "my_booking.status_cancelled",
  CANCEL: "my_booking.status_cancelled",
  PENDING: "my_booking.status_pending",
  ACTIVE: "my_booking.status_succeed",
  HOLD: "my_booking.status_hold",
  FAIL: "my_booking.status_failed",
}

const getStatusColor = (status: BookingStatus) => {
  switch (`${status || ""}`.toUpperCase()) {
    case "COMPLETED":
    case "CONFIRMED":
      return "blue";
    case "ACTIVE":
      return "green";
    case "CANCELLED":
    case "CANCEL":
    case "FAIL":
      return "red";
    case "PENDING":
    case "HOLD":
      return "orange";
    default:
      return "default";
  }
};

const ProductLabel: Record<string, string> = {
  [ProductType.FLIGHT]: "common.flight",
  [ProductType.HOTEL]: "common.hotel",
}

const ProductIcon: Record<string, ReactNode> = {
  [ProductType.FLIGHT]: <FlightV3Icon />,
  [ProductType.HOTEL]: <HotelsIcon />,
}

/** Payment window of a pending order kept locally (no expiryDate), counted from its creation. */
const LOCAL_ORDER_PAYMENT_SECONDS = 3300

interface TripItemProps {
  bookingId: string;
  status: BookingStatus;
  productType: string;
  // Flight properties (the legs are read from orderData.itinData)
  from?: string;
  to?: string;
  startTime?: string;
  endTime?: string;
  airline?: string;
  flightNumber?: string;
  // Hotel properties
  hotelName?: string;
  address?: string;
  checkIn?: string;
  checkOut?: string;
  roomType?: string;
  guestCount?: string;
  rating?: number;
  // Common properties
  totalAmount: string | number;
  orderData: OrderData | any;
  /** Payment tag shown instead of the booking status: awaiting payment / payment expired. */
  paymentState?: TripPaymentState;
  showCheckoutNoti?: boolean,
  shownDelete?: boolean,
  /** Order creation time: the payment countdown runs 55 minutes from it (pending orders kept locally). */
  timmer?: string,
  /** End of the payment countdown (the order's expiryDate), when known (overrides `timmer`). */
  paymentDeadline?: string,
  /** Hide the pay button once this little time (seconds) is left before `paymentDeadline`. */
  minPayRemainingSeconds?: number,
  onClick?: () => void,
  onDelete?: () => void,
}

const TripItem: FC<TripItemProps> = ({
  bookingId,
  status,
  productType,
  shownDelete,
  timmer,
  paymentDeadline,
  minPayRemainingSeconds,
  showCheckoutNoti,
  paymentState,
  onClick,
  onDelete,
  hotelName,
  address,
  checkIn,
  checkOut,
  roomType,
  guestCount,
  totalAmount,
  orderData,
}) => {
  const { t } = useTranslation()
  const { getFormatCurrency, getValidDayjs } = useTETranslation()
  const { token } = theme.useToken()
  const isDesktop = useIsDesktop()
  const router = useTERouter();

  // While the order can be paid, the card is the payment prompt and does not open the order detail.
  const clickable = !!onClick || !showCheckoutNoti

  const handleClick = () => {
    if (onClick) {
      onClick()
      return;
    }
    if (showCheckoutNoti) return
    router.push(productType === ProductType.HOTEL ? `/my-booking/${bookingId}` : `/dat-cho-cua-toi/${bookingId}`);
  };

  const renderStatusTag = () => {
    if (paymentState === "AWAITING") {
      return <Tag color="orange" style={{ marginInlineEnd: 0 }}>{t("my_booking.status_awaiting_payment")}</Tag>
    }
    if (paymentState === "EXPIRED") {
      return <Tag color="red" style={{ marginInlineEnd: 0 }}>{t("my_booking.status_payment_expired")}</Tag>
    }
    return (
      <Tag color={getStatusColor(status)} style={{ marginInlineEnd: 0 }}>
        {t(BookingStatusLabel[status] || status)}
      </Tag>
    )
  }

  const renderFlight = () => {
    const segments: (TypeSegment & Record<string, any>)[] = parseJson(orderData?.itinData || '[]') || []
    const legs = segments.filter((seg) => seg.id === 0).map((seg) => groupSegments(seg, segments) as typeof segments)
    const isRoundTrip = segments.some((seg) => (seg.leg ?? 0) > 0)

    return (
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        {legs.map((leg, idx) => {
          const first = leg[0]
          const last = leg[leg.length - 1]
          const startTime = getDatetimeSegment(first.startTime, first.startTimeZoneOffset)
          const endTime = getDatetimeSegment(last.endTime, last.endTimeZoneOffset)
          const sameDay = startTime.format("DD/MM/YYYY") === endTime.format("DD/MM/YYYY")
          const airlineText = `${first.airlineName || first.airline || ""} ${leg.map((seg) => seg.flightNumber).filter(Boolean).join(", ")}`.trim()
          const legLabel = isRoundTrip || legs.length === 1
            ? t((first.leg ?? 0) > 0 ? "flight.return" : "flight.departure")
            : `${t("flight.flight_short")} ${idx + 1}`

          return (
            <Space key={`${first.flightNumber}-${idx}`} direction="vertical" size={2} style={{ width: "100%" }}>
              <Space size={8} wrap align="center">
                <Text type="secondary" className="te-text-small">{legLabel}</Text>
                <Text strong style={{ fontSize: 16 }}>
                  {first.startPoint} → {last.endPoint}
                </Text>
                {leg.length > 1 && (
                  <Tag style={{ marginInlineEnd: 0 }}>{t("my_booking.stops", { count: leg.length - 1 })}</Tag>
                )}
              </Space>
              {!!first.startPointName && !!last.endPointName && (
                <Text type="secondary">{first.startPointName} → {last.endPointName}</Text>
              )}
              <Text>
                {startTime.format("HH:mm DD/MM/YYYY")} – {sameDay ? endTime.format("HH:mm") : endTime.format("HH:mm DD/MM/YYYY")}
                {isDesktop && <Text type="secondary">{" · "}{airlineText}</Text>}
              </Text>
              {!isDesktop && <Text type="secondary">{airlineText}</Text>}
            </Space>
          )
        })}
      </Space>
    )
  }

  const renderHotel = () => {
    const checkInDate = checkIn ? getValidDayjs(checkIn) : null
    const checkOutDate = checkOut ? getValidDayjs(checkOut) : null
    const nights = checkInDate && checkOutDate ? checkOutDate.startOf("day").diff(checkInDate.startOf("day"), "day") : 0

    return (
      <Space orientation="vertical" size={4} style={{ width: "100%" }}>
        <Text strong style={{ fontSize: 16 }}>{hotelName || t("common.hotel")}</Text>
        {!!address && <Text type="secondary" ellipsis={{ tooltip: address }}>{address}</Text>}
        {checkInDate && checkOutDate && (
          <Text>
            {checkInDate.format("ddd, DD/MM/YYYY")} → {checkOutDate.format("ddd, DD/MM/YYYY")}
            {nights > 0 && <Text type="secondary" style={{ whiteSpace: "nowrap" }}>{" · "}{t("my_booking.nights", { count: nights })}</Text>}
          </Text>
        )}
        {(!!roomType || !!guestCount) && (
          <Text type="secondary">{[roomType, guestCount].filter(Boolean).join(" · ")}</Text>
        )}
      </Space>
    )
  }

  const renderBookingContent = () => {
    switch (productType) {
      case ProductType.HOTEL:
        return renderHotel()
      default:
        return renderFlight()
    }
  }

  return (
    <Card
      style={{
        width: "100%",
        height: "100%",
        cursor: clickable ? "pointer" : undefined,
        ...(paymentState === "AWAITING" ? { borderColor: token.colorWarningBorder } : {}),
      }}
      styles={{ body: { padding: isDesktop ? 16 : 12 } }}
      variant="outlined"
      onClick={handleClick}
      hoverable={clickable}
    >
      <Row justify="space-between" align="middle" gutter={[8, 8]}>
        <Col flex="auto" style={{ minWidth: 0 }}>
          <Space size={10} align="center">
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: token.colorPrimaryBg,
                color: token.colorPrimary,
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              {ProductIcon[productType] || ProductIcon[ProductType.FLIGHT]}
            </span>
            <Space orientation="vertical" size={0}>
              <Text type="secondary" className="te-text-small">
                {t(ProductLabel[productType] || "common.other")}
              </Text>
              {/* Copying the code must not open the order. */}
              <span onClick={(e) => e.stopPropagation()}>
                <Text strong copyable={{ text: bookingId }}>{bookingId}</Text>
              </span>
            </Space>
          </Space>
        </Col>
        <Col flex="none">
          <Space size={4} align="center" style={{ whiteSpace: "nowrap" }}>
            {renderStatusTag()}
            {shownDelete && (
              <Button
                type="text"
                danger
                icon={<FontAwesomeIcon icon={faTrash} />}
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete?.()
                }}
                size="small"
                aria-label={t("common.delete")}
                title={t("common.delete")}
              />
            )}
          </Space>
        </Col>
      </Row>

      <div style={{ marginBlock: 12 }}>{renderBookingContent()}</div>

      <Row justify="space-between" align="middle" gutter={[8, 4]}>
        <Col xs={24} sm={{ flex: "none" }}>
          {!!orderData?.createdDate && (
            <Text type="secondary" className="te-text-small">
              {t("my_booking.created_at", { date: getValidDayjs(orderData.createdDate).format("HH:mm DD/MM/YYYY") })}
            </Text>
          )}
        </Col>
        <Col xs={24} sm={{ flex: "none" }}>
          <Space size={8} align="baseline" style={{ width: "100%", justifyContent: isDesktop ? undefined : "space-between" }}>
            <Text type="secondary">{t("booking.total_amount_payment")}</Text>
            <Text strong style={{ color: token.colorError, fontSize: 16 }}>
              {getFormatCurrency(totalAmount)}
            </Text>
          </Space>
        </Col>
      </Row>

      {showCheckoutNoti && (paymentDeadline || timmer) && (
        <TimmerCheckout
          onDelete={onDelete}
          deadline={paymentDeadline || getValidDayjs(timmer).add(LOCAL_ORDER_PAYMENT_SECONDS, "second").toISOString()}
          minPayRemainingSeconds={minPayRemainingSeconds}
          orderCode={bookingId}
          productType={productType}
          orderData={orderData}
        />
      )}
    </Card>
  );
};

export default TripItem;
